import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { Building2, Eye, FileImage, Loader2, MessageCircle, PenLine, Upload, X } from 'lucide-react'
import { CertificatePreviewDialog } from '@/components/certificate/CertificatePreviewDialog'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useMyCompany,
  useUpdateMyCompany,
  type UpdateMyCompanyDTO,
} from '@/services/admin/companies.service'
import type { SupportChannelType } from '@/types/auth'
import {
  useUploadCompanyLogo,
  useUploadCertificateBackground,
  useUploadCertificateLogo,
  useUploadCertificateSignature,
} from '@/services/upload.service'
import { useToast } from '@/hooks/use-toast'
import { getErrorMessage } from '@/utils/error'

/**
 * Tela "Minha Empresa" pro admin REGULAR. Edita dados da própria
 * empresa que serão usados no certificado de conclusão (fundo,
 * assinatura, nome do diretor) + identidade visual básica (logo,
 * nome).
 *
 * Slug/domain/active continuam exclusivos do super_admin (rota
 * /admin/companies/:id). Aqui consumimos /admin/companies/me.
 */
export function MyCompanySettings() {
  const { data: company, isLoading, isError } = useMyCompany()
  const update = useUpdateMyCompany()
  const uploadLogo = useUploadCompanyLogo()
  const uploadBg = useUploadCertificateBackground()
  const uploadSig = useUploadCertificateSignature()
  const uploadCertLogo = useUploadCertificateLogo()
  const { toast } = useToast()

  const logoInputRef = useRef<HTMLInputElement>(null)
  const bgInputRef = useRef<HTMLInputElement>(null)
  const sigInputRef = useRef<HTMLInputElement>(null)
  const certLogoInputRef = useRef<HTMLInputElement>(null)

  // Estado local pra permitir edição sem persistir até clicar "Salvar"
  const [draft, setDraft] = useState<UpdateMyCompanyDTO>({})

  // Controla a abertura do preview. Garante que a visualização passa pelo
  // MESMO componente que o aluno vê e que o super_admin pré-visualiza:
  // `CertificatePreviewDialog` em `@/components/certificate/`.
  const [previewOpen, setPreviewOpen] = useState(false)

  // Sincroniza draft quando company carrega — sobrescreve só se não há
  // edição pendente (evita perder o que user digitou se refetch acontecer)
  useEffect(() => {
    if (company && Object.keys(draft).length === 0) {
      setDraft({
        name: company.name,
        logo: company.logo ?? null,
        certificateBackgroundUrl: company.certificateBackgroundUrl ?? null,
        certificateLogoUrl: company.certificateLogoUrl ?? null,
        certificateSignatureUrl: company.certificateSignatureUrl ?? null,
        directorName: company.directorName ?? null,
        directorRole: company.directorRole ?? null,
        includeGlobalSignature: company.includeGlobalSignature ?? false,
        supportChannelType: company.supportChannelType ?? null,
        supportChannelUrl: company.supportChannelUrl ?? null,
        supportChannelLabel: company.supportChannelLabel ?? null,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company])

  function patch<K extends keyof UpdateMyCompanyDTO>(key: K, value: UpdateMyCompanyDTO[K]) {
    setDraft((d) => ({ ...d, [key]: value }))
  }

  async function handleLogoChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !company) return
    try {
      const url = await uploadLogo.mutateAsync({ file, companyId: company.id })
      patch('logo', url)
      toast({ title: 'Logo enviado' })
    } catch (err: unknown) {
      toast({ variant: 'destructive', title: 'Erro', description: getErrorMessage(err) })
    } finally {
      if (logoInputRef.current) logoInputRef.current.value = ''
    }
  }

  async function handleBgChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !company) return
    try {
      const url = await uploadBg.mutateAsync({ file, companyId: company.id })
      patch('certificateBackgroundUrl', url)
      toast({ title: 'Fundo do certificado enviado' })
    } catch (err: unknown) {
      toast({ variant: 'destructive', title: 'Erro', description: getErrorMessage(err) })
    } finally {
      if (bgInputRef.current) bgInputRef.current.value = ''
    }
  }

  async function handleCertLogoChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !company) return
    try {
      const url = await uploadCertLogo.mutateAsync({ file, companyId: company.id })
      patch('certificateLogoUrl', url)
      toast({ title: 'Logo do certificado enviada' })
    } catch (err: unknown) {
      toast({ variant: 'destructive', title: 'Erro', description: getErrorMessage(err) })
    } finally {
      if (certLogoInputRef.current) certLogoInputRef.current.value = ''
    }
  }

  async function handleSigChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !company) return
    try {
      const url = await uploadSig.mutateAsync({ file, companyId: company.id })
      patch('certificateSignatureUrl', url)
      toast({ title: 'Assinatura enviada' })
    } catch (err: unknown) {
      toast({ variant: 'destructive', title: 'Erro', description: getErrorMessage(err) })
    } finally {
      if (sigInputRef.current) sigInputRef.current.value = ''
    }
  }

  async function handleSave() {
    try {
      await update.mutateAsync(draft)
      toast({ title: 'Empresa atualizada' })
    } catch (err: unknown) {
      toast({
        variant: 'destructive',
        title: 'Erro ao salvar',
        description: getErrorMessage(err),
      })
    }
  }

  if (isLoading) {
    return (
      <Card className="glass-card border-border/50">
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (isError || !company) {
    return (
      <Card className="glass-card border-border/50">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Não foi possível carregar os dados da empresa.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card className="glass-card border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Identidade da empresa
          </CardTitle>
          <CardDescription>
            Logo e nome aparecem em vários lugares da plataforma. Slug e domínio são controlados
            pelo super_admin.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex items-center gap-4">
              {draft.logo ? (
                <img
                  src={draft.logo}
                  alt=""
                  className="w-16 h-16 rounded-lg object-contain border border-border/40"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-muted/40 border border-dashed border-border flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-muted-foreground" />
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleLogoChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadLogo.isPending}
                  className="gap-1.5"
                >
                  {uploadLogo.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  {draft.logo ? 'Trocar' : 'Enviar'}
                </Button>
                {draft.logo && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => patch('logo', null)}
                    className="gap-1.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={draft.name ?? ''}
              onChange={(e) => patch('name', e.target.value)}
              placeholder="Nome da empresa"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card border-border/50">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1.5">
              <CardTitle className="flex items-center gap-2">
                <FileImage className="w-5 h-5 text-primary" />
                Certificado de conclusão
              </CardTitle>
              <CardDescription>
                Dados usados quando um aluno desta empresa baixa o certificado de um curso
                concluído. Campos não preenchidos usam o template global FCamara.
              </CardDescription>
            </div>
            {/* Preview reusa o MESMO CertificatePreviewDialog do super_admin
                (movido pra @/components/certificate/) — render uniforme em
                todas as superfícies que mostram certificado. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5 shrink-0"
              onClick={() => setPreviewOpen(true)}
              disabled={!company}
            >
              <Eye className="w-3.5 h-3.5" />
              Visualizar certificado
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Fundo do certificado (PNG/JPG/WebP, máx 5MB)</Label>
            <p className="text-xs text-muted-foreground">
              Redimensionado pra 1240×877 (A4 paisagem). Use um template com áreas vazias pra nome
              do aluno, curso e assinatura.
            </p>
            <div className="flex items-center gap-4">
              {draft.certificateBackgroundUrl ? (
                <img
                  src={draft.certificateBackgroundUrl}
                  alt=""
                  className="w-32 h-20 rounded object-cover border border-border/40"
                />
              ) : (
                <div className="w-32 h-20 rounded bg-muted/40 border border-dashed border-border flex items-center justify-center">
                  <FileImage className="w-6 h-6 text-muted-foreground" />
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={bgInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleBgChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => bgInputRef.current?.click()}
                  disabled={uploadBg.isPending}
                  className="gap-1.5"
                >
                  {uploadBg.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  {draft.certificateBackgroundUrl ? 'Trocar' : 'Enviar'}
                </Button>
                {draft.certificateBackgroundUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => patch('certificateBackgroundUrl', null)}
                    className="gap-1.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Logo do certificado (PNG/JPG/WebP, máx 5MB)</Label>
            <p className="text-xs text-muted-foreground">
              Logo própria do certificado, separada do logo da empresa. Redimensionada
              proporcionalmente pra ~800px de largura. PNG transparente é o ideal — o fundo do
              certificado é escuro.
            </p>
            <div className="flex items-center gap-4">
              {draft.certificateLogoUrl ? (
                <img
                  src={draft.certificateLogoUrl}
                  alt=""
                  className="w-32 h-20 rounded object-contain border border-border/40 bg-white/5"
                />
              ) : (
                <div className="w-32 h-20 rounded bg-muted/40 border border-dashed border-border flex items-center justify-center">
                  <FileImage className="w-6 h-6 text-muted-foreground" />
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={certLogoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleCertLogoChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => certLogoInputRef.current?.click()}
                  disabled={uploadCertLogo.isPending}
                  className="gap-1.5"
                >
                  {uploadCertLogo.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  {draft.certificateLogoUrl ? 'Trocar' : 'Enviar'}
                </Button>
                {draft.certificateLogoUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => patch('certificateLogoUrl', null)}
                    className="gap-1.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="directorName">Representante da empresa</Label>
            <Input
              id="directorName"
              value={draft.directorName ?? ''}
              onChange={(e) => patch('directorName', e.target.value || null)}
              placeholder="Ex.: João da Silva"
            />
            <p className="text-xs text-muted-foreground">
              Aparece embaixo da assinatura no certificado.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="directorRole">Cargo do representante</Label>
            <Input
              id="directorRole"
              value={draft.directorRole ?? ''}
              onChange={(e) => patch('directorRole', e.target.value || null)}
              placeholder="Ex.: Diretor de Pessoas"
            />
          </div>

          <div className="space-y-2">
            <Label>Assinatura do diretor (máx 5MB)</Label>
            <p className="text-xs text-muted-foreground">
              Redimensionada pra 600×200. Recomendado PNG com fundo transparente — assinatura
              digitalizada sobre fundo branco fica feio sobre o certificado.
            </p>
            <div className="flex items-center gap-4">
              {draft.certificateSignatureUrl ? (
                <img
                  src={draft.certificateSignatureUrl}
                  alt=""
                  className="w-32 h-12 rounded object-contain border border-border/40 bg-white/5"
                />
              ) : (
                <div className="w-32 h-12 rounded bg-muted/40 border border-dashed border-border flex items-center justify-center">
                  <PenLine className="w-5 h-5 text-muted-foreground" />
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={sigInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleSigChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => sigInputRef.current?.click()}
                  disabled={uploadSig.isPending}
                  className="gap-1.5"
                >
                  {uploadSig.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  {draft.certificateSignatureUrl ? 'Trocar' : 'Enviar'}
                </Button>
                {draft.certificateSignatureUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => patch('certificateSignatureUrl', null)}
                    className="gap-1.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer select-none pt-2 border-t border-border/40">
            <Checkbox
              checked={draft.includeGlobalSignature === true}
              onCheckedChange={(checked) => patch('includeGlobalSignature', checked === true)}
              className="mt-0.5"
            />
            <span className="flex-1">
              <span className="text-sm font-medium block">Incluir assinatura global FCamara</span>
              <span className="text-xs text-muted-foreground">
                Quando marcado, o certificado terá a assinatura do diretor da FCamara ao lado da
                assinatura da empresa. Útil pra cursos co-validados.
              </span>
            </span>
          </label>
        </CardContent>
      </Card>

      <Card className="glass-card border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            Canal de atendimento
          </CardTitle>
          <CardDescription>
            Configura o botão flutuante de suporte que os alunos desta empresa veem. Deixe como
            &quot;Nenhum&quot; para não exibir o botão.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select
              value={draft.supportChannelType ?? 'none'}
              onValueChange={(v) => {
                patch('supportChannelType', v === 'none' ? null : (v as SupportChannelType))
                if (v === 'none') patch('supportChannelUrl', null)
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhum</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="teams">Microsoft Teams</SelectItem>
                <SelectItem value="slack">Slack</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {draft.supportChannelType && (
            <>
              <div className="space-y-2">
                <Label htmlFor="supportChannelUrl">Link do grupo / canal</Label>
                <Input
                  id="supportChannelUrl"
                  value={draft.supportChannelUrl ?? ''}
                  onChange={(e) => patch('supportChannelUrl', e.target.value || null)}
                  placeholder="https://chat.whatsapp.com/..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="supportChannelLabel">Texto do card (opcional)</Label>
                <Input
                  id="supportChannelLabel"
                  value={draft.supportChannelLabel ?? ''}
                  onChange={(e) => patch('supportChannelLabel', e.target.value || null)}
                  placeholder="Tire suas dúvidas com a comunidade."
                  maxLength={120}
                />
                <p className="text-xs text-muted-foreground">
                  Aparece na descrição do card. Vazio cai num texto genérico.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={update.isPending} className="gap-2">
          {update.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Salvar alterações
        </Button>
      </div>

      {/* Preview do certificado — mesmo dialog do super_admin (movido pra
          @/components/certificate/). Garante render uniforme. Passa o
          `company` direto (forma estrutural cobre Company tanto do admin
          quanto do super-admin via CompanyInput). */}
      <CertificatePreviewDialog
        company={previewOpen && company ? company : null}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  )
}
